import { Controller } from "@hotwired/stimulus"

// Renders the admin zip choropleth (#696). Loads Leaflet from CDN on connect
// (mirroring autocomplete_controller's Google Maps injection), reads the
// server-built FeatureCollection + color scale from an inline JSON script tag,
// and draws the shaded ZCTA polygons, popups, permanent ZIP labels, and legend.
// All data is server-derived; this file only renders it.
const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"
const LEAFLET_JS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"
const TILES = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
const ATTRIBUTION = "&copy; OpenStreetMap contributors &copy; CARTO &mdash; Boundaries: US Census ZCTA (2010)"

export default class extends Controller {
  static targets = ["map", "data"]

  connect() {
    this.config = JSON.parse(this.dataTarget.textContent)
    this.loadLeaflet()
      .then(() => this.render())
      .catch((error) => console.error("Failed to load Leaflet:", error))
  }

  // Tear the map down so a Turbo cache restore (the admin pages use Turbo)
  // can't leave a stale map or double-init on reconnect.
  disconnect() {
    this.map?.remove()
    this.map = null
  }

  loadLeaflet() {
    if (window.L) return Promise.resolve()
    if (window.__leafletLoading) return window.__leafletLoading

    window.__leafletLoading = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href='${LEAFLET_CSS}']`)) {
        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = LEAFLET_CSS
        document.head.appendChild(link)
      }
      const script = document.createElement("script")
      script.src = LEAFLET_JS
      script.async = true
      script.onload = () => resolve()
      script.onerror = reject
      document.head.appendChild(script)
    })

    return window.__leafletLoading
  }

  render() {
    const L = window.L
    const { featureCollection, buckets, colors, labels } = this.config

    const map = L.map(this.mapTarget)
    this.map = map
    L.tileLayer(TILES, { maxZoom: 18, attribution: ATTRIBUTION }).addTo(map)

    const color = (count) => {
      for (let i = buckets.length - 1; i >= 0; i--) if (count >= buckets[i]) return colors[i]
      return colors[0]
    }

    const layer = L.geoJSON(featureCollection, {
      style: (f) => ({ color: "#7a3b00", weight: 1, fillColor: color(f.properties.count), fillOpacity: 0.65 }),
      onEachFeature: (f, l) => {
        l.bindPopup('<div class="zip-tip"><b>' + f.properties.zip + "</b><br>Count: <b>" + f.properties.count + "</b></div>")
        l.on("mouseover", () => l.setStyle({ weight: 2.5, fillOpacity: 0.85 }))
        l.on("mouseout", () => l.setStyle({ weight: 1, fillOpacity: 0.65 }))
        l.bindTooltip(String(f.properties.zip), { permanent: true, direction: "center", className: "ziplabel" })
      }
    }).addTo(map)

    // Every rendered feature is Colorado (out-of-region ZIPs are excluded
    // server-side), so fitting to the layer bounds gives the CO framing the
    // templates hardcoded via a "80" prefix.
    if (featureCollection.features.length) {
      map.fitBounds(layer.getBounds().pad(0.05))
    } else {
      map.setView([40.0, -105.1], 9)
    }

    const legend = L.control({ position: "bottomright" })
    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "legend")
      let rows = "<b>Count</b><br>"
      for (let i = 0; i < colors.length; i++) rows += '<i style="background:' + colors[i] + '"></i>' + labels[i] + "<br>"
      div.innerHTML = rows
      return div
    }
    legend.addTo(map)
  }
}
